import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  AuthUser,
  canAccessAdmin as canAccessAdminForUser,
  canAccessTracker as canAccessTrackerForUser,
  hasActiveSubscription as hasActiveSubscriptionForUser,
  initializeAuthSession,
  isAdmin as isAdminForUser,
  isLoggedIn as isLoggedInForUser,
  loginUserWithGoogle,
  loginUser,
  logoutUser,
  registerUser,
  registerUserWithGoogle,
  updateCurrentUser as updateStoredCurrentUser,
} from './services/authService';

interface AuthContextType {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<AuthUser>;
  loginWithGoogle: (credential: string) => Promise<AuthUser>;
  register: (email: string, password: string, referralCode?: string) => Promise<AuthUser>;
  registerWithGoogle: (credential: string, referralCode?: string) => Promise<AuthUser>;
  logout: () => void;
  getCurrentUser: () => AuthUser | null;
  updateCurrentUser: (user: AuthUser) => AuthUser;
  isLoggedIn: () => boolean;
  hasActiveSubscription: () => boolean;
  isPaidUser: () => boolean;
  isAdmin: () => boolean;
  canAccessTracker: () => boolean;
  canAccessAdmin: () => boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const hydrateAuthState = async () => {
      const nextUser = await initializeAuthSession();
      if (!isMounted) {
        return;
      }
      setCurrentUser(nextUser);
      setIsLoading(false);
    };

    void hydrateAuthState();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const nextUser = await loginUser(email, password);
    setCurrentUser(nextUser);
    return nextUser;
  }, []);

  const loginWithGoogle = useCallback(async (credential: string) => {
    const nextUser = await loginUserWithGoogle(credential);
    setCurrentUser(nextUser);
    return nextUser;
  }, []);

  const register = useCallback(async (email: string, password: string, referralCode?: string) => {
    const nextUser = await registerUser({ email, password, referralCode });
    setCurrentUser(nextUser);
    return nextUser;
  }, []);

  const registerWithGoogle = useCallback(async (credential: string, referralCode?: string) => {
    const nextUser = await registerUserWithGoogle(credential, referralCode);
    setCurrentUser(nextUser);
    return nextUser;
  }, []);

  const logout = useCallback(() => {
    logoutUser();
    setCurrentUser(null);
  }, []);

  const updateCurrentUser = useCallback((user: AuthUser) => {
    const nextUser = updateStoredCurrentUser(user);
    setCurrentUser(nextUser);
    return nextUser;
  }, []);

  const getCurrentUser = () => currentUser;
  const isLoggedIn = () => isLoggedInForUser(currentUser);
  const hasActiveSubscription = () => hasActiveSubscriptionForUser(currentUser);
  const isPaidUser = () => hasActiveSubscription();
  const isAdmin = () => isAdminForUser(currentUser);
  const canAccessTracker = () => canAccessTrackerForUser(currentUser);
  const canAccessAdmin = () => canAccessAdminForUser(currentUser);

  return (
    <AuthContext.Provider
      value={{
        user: currentUser,
        login,
        loginWithGoogle,
        register,
        registerWithGoogle,
        logout,
        getCurrentUser,
        updateCurrentUser,
        isLoggedIn,
        hasActiveSubscription,
        isPaidUser,
        isAdmin,
        canAccessTracker,
        canAccessAdmin,
        isLoading,
        isAuthenticated: isLoggedIn(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
